import { useState, useEffect, useCallback, useMemo } from "react";
import { CreditsSummaryTotalEntity } from "../../domain/entities/CreditSummaryEntity";
import { BankPaymentSummaryEntity } from "../../domain/entities/BankPaymentSummaryEntity";
import { recoContainer } from "../../di/container";
import { QueryCountsEntity } from "../../../../shared/queries/domain/entities/QueryCountsEntity";
import { GetMyCountsUseCase } from "../../../../shared/queries/domain/use-cases/GetMyCountsUseCase";
import { QueriesRepositoryImpl } from "../../../../shared/queries/data/repositories/QueriesRepositoryImpl";
import { queriesApi } from "../../../../shared/queries/data/api/queriesApi";

interface RecoSummaryData {
  customerCredits: CreditsSummaryTotalEntity | null;
  vendorCredits: CreditsSummaryTotalEntity | null;
  bankPayments: BankPaymentSummaryEntity | null;
  queryCounts: QueryCountsEntity | null;
}

export const useRecoSummary = () => {
  const [data, setData] = useState<RecoSummaryData>({
    customerCredits: null,
    vendorCredits: null,
    bankPayments: null,
    queryCounts: null,
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Instantiate queries repository and use case
  const queriesRepository = useMemo(
    () => new QueriesRepositoryImpl(queriesApi),
    []
  );
  const getQueryCountsUseCase = useMemo(
    () => new GetMyCountsUseCase(queriesRepository),
    [queriesRepository]
  );

  const fetchAllSummaries = useCallback(
    async (autoRefresh = false) => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all summaries in parallel using Promise.all
        const [
          customerCreditsData,
          vendorCreditsData,
          bankPaymentsData,
          queryCountsResult,
        ] = await Promise.all([
          recoContainer.getCustomerCreditsSummary().execute(autoRefresh),
          recoContainer.getVendorCreditsSummary().execute(autoRefresh),
          recoContainer.getBankPaymentsSummary().execute(),
          getQueryCountsUseCase.execute(),
        ]);

        setData({
          customerCredits: customerCreditsData,
          vendorCredits: vendorCreditsData,
          bankPayments: bankPaymentsData,
          queryCounts:
            queryCountsResult.isSuccess ? queryCountsResult.value : null,
        });
      } catch (err: any) {
        setError(err.message || "Failed to fetch reco summaries");
        console.error("Error fetching reco summaries:", err);
      } finally {
        setLoading(false);
      }
    },
    [getQueryCountsUseCase]
  );

  const refreshAllCaches = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);

      // Refresh both customer and vendor caches in parallel
      await Promise.all([
        recoContainer.refreshCustomerCreditsCache().execute(),
        recoContainer.refreshVendorCreditsCache().execute(),
      ]);

      // After refresh, fetch updated summaries
      await fetchAllSummaries(false);
    } catch (err: any) {
      setError(err.message || "Failed to refresh credits caches");
      console.error("Error refreshing credits caches:", err);
    } finally {
      setRefreshing(false);
    }
  }, [fetchAllSummaries]);

  const refreshCustomerCache = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      await recoContainer.refreshCustomerCreditsCache().execute();
      await fetchAllSummaries(false);
    } catch (err: any) {
      setError(err.message || "Failed to refresh customer credits cache");
      console.error("Error refreshing customer credits:", err);
    } finally {
      setRefreshing(false);
    }
  }, [fetchAllSummaries]);

  const refreshVendorCache = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      await recoContainer.refreshVendorCreditsCache().execute();
      await fetchAllSummaries(false);
    } catch (err: any) {
      setError(err.message || "Failed to refresh vendor credits cache");
      console.error("Error refreshing vendor credits:", err);
    } finally {
      setRefreshing(false);
    }
  }, [fetchAllSummaries]);

  useEffect(() => {
    fetchAllSummaries(false);
  }, [fetchAllSummaries]);

  return {
    customerCredits: data.customerCredits,
    vendorCredits: data.vendorCredits,
    bankPayments: data.bankPayments,
    queryCounts: data.queryCounts,
    loading,
    refreshing,
    error,
    fetchAllSummaries,
    refreshAllCaches,
    refreshCustomerCache,
    refreshVendorCache,
  };
};

